import React, { useState, useEffect } from 'react'
import { CircularProgress } from '@mui/material'

import { CButton, CFormInput, CInputGroup, CFormLabel, CFormSelect } from '@coreui/react'
import { useNavigate } from 'react-router-dom'
import { IoMdArrowRoundBack } from 'react-icons/io'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import axios from 'axios'
import { validateEntityName } from '../../../helpers/validation'

const StartSCAScan = () => {
  const navigate = useNavigate()

  const [scanName, setScanName] = useState('')

  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [applicationId, setApplicationId] = useState('')
  const [applications, setApplications] = useState([])
  const [progressMessage, setProgressMessage] = useState('')
  const [errors, setErrors] = useState({
    scanName: '',
    applicationId: '',
    file: '',
    general: '',
  })

  const [submissionSuccess, setSubmissionSuccess] = useState(false)

  const [selectedFile, setSelectedFile] = useState(null)

  // const onFileChange = (e) => {
  //   const file = e.target.files[0]
  //   setSelectedFile(file)
  //   handleFileChange(e) // if you're passing this prop from parent to handle logic
  // }

  function isExceeding500MB(file) {
    const fileSizeInBytes = file.size
    const fileSizeInMB = fileSizeInBytes / (1024 * 1024)
    return fileSizeInMB > 500
  }

  useEffect(() => {
    fetchApplications()
  }, [])

  const fetchApplications = async () => {
    setLoading(true)
    setProgressMessage('Setting up...')
    const token = sessionStorage.getItem('ASIToken')
    const response = await axios.get(`/api/v1/applications/all`, {
      headers: { Authorization: `Bearer ${token}` },
    })

    setApplications(response.data.applications)

    if (response.data.applications.length > 0) {
      setApplicationId(response.data.applications[0]._id)
    } else {
      setApplicationId('')
    }

    setLoading(false)
  }

  const validateForm = () => {
    let isValid = true
    let newErrors = {
      scanName: '',
      applicationId: '',
      file: '',
      general: '',
    }

    if (!validateEntityName(scanName)) {
      newErrors.scanName =
        'Invalid scan name. Only letters, numbers, spaces, underscores, dots, #, @ and hyphens are allowed (max 100 characters).'
      isValid = false
    }

    if (applicationId === '') {
      newErrors.applicationId = 'Please select an application'
      isValid = false
    }

    if (file === null) {
      newErrors.file = 'Please select a project zip file'
      isValid = false
    } else if (isExceeding500MB(file)) {
      newErrors.file =
        'The file size cannot be more than 500MB. Please reach out to customer support.'
      isValid = false
    }

    setErrors(newErrors)
    return isValid
  }

  const startScan = () => {
    if (validateForm()) {
      setLoading(true)
      setProgressMessage('Running Software Composition Analysis Scan...')

      const bearerToken = sessionStorage.getItem('ASIToken')

      const formData = new FormData()
      formData.append('scanName', scanName)
      formData.append('projectZipFile', file)
      formData.append('applicationId', applicationId)

      fetch(global.backendUrl + '/api/v1/sca-scans', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
        body: formData,
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.hasOwnProperty('error')) {
            setErrors((prevErrors) => ({ ...prevErrors, general: data.error }))
            setLoading(false)
          } else if (data.hasOwnProperty('err')) {
            setLoading(false)
            setErrors((prevErrors) => ({
              ...prevErrors,
              general: 'Something went wrong. Please try again',
            }))
          } else {
            setSubmissionSuccess(true)
            setLoading(false)
            toast('Scan Started', {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: 'light',
            })
            navigate('/sca-scans')
          }
        })
        .catch((error) => {
          console.error(error)
          setLoading(false)
          setErrors((prevErrors) => ({
            ...prevErrors,
            general: 'An error occurred. Please try again.',
          }))
        })
    }
  }

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0]
    setFile(selectedFile)
  }

  const goBack = (e) => {
    e.preventDefault()
    navigate('/sca-scans')
  }

  if (loading) {
    return (
      <div
        className="loading-spinner"
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <div
          style={{
            width: '50px',
            height: '50px',
            border: '5px solid #f3f3f3',
            borderTop: '5px solid #e50202',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        ></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <span style={{ color: '#fff', marginTop: 15 }}>{progressMessage}</span>
      </div>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        overflow: 'scroll',
        position: 'relative',
        overflowY: 'hidden',
        overflowX: 'hidden',
      }}
    >
      <div style={{ width: '60%' }}>
        <div>
          <div
            style={{
              marginBottom: '0rem',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <h4>
              <strong>Start an SCA Scan</strong>
            </h4>
            <CButton
              onClick={goBack}
              style={{
                width: 200,
                marginBottom: '2%',
                marginRight: 20,
                // borderWidth: 0,
                fontSize: 13,
                borderColor: '#fff',
                borderWidth: 1,
                color: '#fff',
                background: 'transparent',
              }}
              color="primary"
              className="px-3"
            >
              <IoMdArrowRoundBack size={25} style={{ color: '#fff', marginRight: 10 }} />
              Back to SCA Scans
            </CButton>
          </div>

          <div style={{ width: '100%', backgroundColor: '#252B3B', padding: 15 }}>
            <CFormLabel htmlFor="formFileSm" style={{ marginTop: 30, color: 'white' }}>
              Scan Name
            </CFormLabel>
            <CInputGroup className="" style={{ flexDirection: 'column' }}>
              <CFormInput
                placeholder="Scan Name"
                autoComplete="scanName"
                className="white-input"
                onChange={(e) => setScanName(e.target.value)}
                style={{ width: '100%' }}
                maxLength={100}
              />
              {errors.scanName && (
                <div style={{ color: 'red', marginTop: '5px' }}>{errors.scanName}</div>
              )}
            </CInputGroup>
            <CFormLabel htmlFor="formFileSm" style={{ marginTop: 30, color: 'white' }}>
              Select application
            </CFormLabel>
            <CInputGroup className="" style={{ flexDirection: 'column' }}>
              <CFormSelect
                id="scanType"
                className="white-input"
                onChange={(e) => setApplicationId(e.target.value)}
                style={{ width: '100%' }}
              >
                {applications.map((application) => (
                  <option key={application._id} value={application._id}>
                    {application.name}
                  </option>
                ))}
              </CFormSelect>
              {errors.applicationId && (
                <div style={{ color: 'red', marginTop: '5px' }}>{errors.applicationId}</div>
              )}
            </CInputGroup>
            <CFormLabel htmlFor="formFileSm" style={{ marginTop: 30, color: 'white' }}>
              Upload the project zip file
            </CFormLabel>
            <br />
            {/* <CInputGroup className="" style={{ flexDirection: 'column', }}>
              <CFormInput
                placeholder="Upload the project zip file"
                autoComplete="username"
                type="file" 
                className="white-input"
                size="lg"
                id="inputFile"
                accept="application/zip"
                onChange={handleFileChange}
                style={{ width: '100%' }}
              />
              {errors.file && <div style={{ color: 'red', marginTop: '5px' }}>{errors.file}</div>}
            </CInputGroup>           */}
            {/* <CInputGroup style={{ flexDirection: 'column', width: '100%' }}>
                  <label htmlFor="inputFile" style={{ 
                    padding: '20px', 
                    border: '2px dashed #ccc', 
                    borderRadius: '10px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: '#f9f9f9',
                    fontSize: '16px'
                  }}>
                    {selectedFile ? selectedFile.name : "Click here to upload a project zip file"}
                    <CFormInput
                      type="file"
                      id="inputFile"
                      accept="application/zip"
                      onChange={onFileChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                  {errors.file && <div style={{ color: 'red', marginTop: '5px' }}>{errors.file}</div>}
                </CInputGroup> */}
            {/* <CInputGroup style={{ flexDirection: 'column', width: '100%' }}>
              <div
                onDrop={(e) => {
                  const file = e.dataTransfer.files[0]
                  const fileName = file.name
                  const extension = fileName.split('.').pop().toLowerCase()

                  console.log('File extension:', extension)
                  e.preventDefault()
                  e.stopPropagation()
                  const droppedFile = e.dataTransfer.files[0]
                  console.log('Dropped file:', droppedFile)

                  if (droppedFile && droppedFile.name.endsWith('.zip')) {
                    setSelectedFile(droppedFile)
                    setFile(droppedFile)
                  } else {
                    setErrors((prev) => ({ ...prev, file: 'Only .zip files are allowed.' }))
                  }

                  // if (droppedFile && droppedFile.type === 'application/zip') {
                  //   setSelectedFile(droppedFile)
                  //   setFile(droppedFile)
                  // } else {
                  //   setErrors((prev) => ({ ...prev, file: 'Only .zip files are allowed.' }))
                  // }
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                onDragEnter={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  e.currentTarget.style.backgroundColor = '#e6e6e6'
                }}
                onDragLeave={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  e.currentTarget.style.backgroundColor = '#f9f9f9'
                }}
                style={{
                  padding: '20px',
                  border: '2px dashed #ccc',
                  borderRadius: '10px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#f9f9f9',
                  fontSize: '16px',
                  height: '150px', // Increased height
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => document.getElementById('inputFile').click()}
              >
                {selectedFile
                  ? selectedFile.name
                  : 'Drag & drop a .zip file here or click to upload'}
                <CFormInput
                  type="file"
                  id="inputFile"
                  accept="application/zip"
                  onChange={onFileChange}
                  style={{ display: 'none' }}
                />
              </div>
              {errors.file && <div style={{ color: 'red', marginTop: '5px' }}>{errors.file}</div>}
            </CInputGroup> */}
            <CInputGroup style={{ flexDirection: 'column', width: '100%' }}>
              <div
                onDrop={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  e.currentTarget.style.backgroundColor = '#f9f9f9'
                  e.currentTarget.style.borderColor = '#ccc'

                  if (!e.dataTransfer.files.length) return

                  const droppedFile = e.dataTransfer.files[0]
                  const fileName = droppedFile.name.toLowerCase()

                  if (fileName.endsWith('.zip')) {
                    setSelectedFile(droppedFile)
                    setFile(droppedFile)
                    setErrors((prev) => ({ ...prev, file: '' }))
                  } else {
                    setErrors((prev) => ({ ...prev, file: 'Only .zip files are allowed.' }))
                  }
                }}
                onDragOver={(e) => e.preventDefault()}
                onDragEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e6e6e6'
                  e.currentTarget.style.borderColor = '#e50202'
                }}
                onDragLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9f9f9'
                  e.currentTarget.style.borderColor = '#ccc'
                }}
                style={{
                  padding: '20px',
                  border: '2px dashed #ccc',
                  borderRadius: '10px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#f9f9f9',
                  fontSize: '16px',
                  height: '150px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                }}
              >
                {selectedFile ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                      style={{
                        maxWidth: '80%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {selectedFile.name}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedFile(null)
                        setFile(null)
                        setErrors((prev) => ({ ...prev, file: '' }))
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#ff4444',
                        marginLeft: '10px',
                        fontSize: '18px',
                      }}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <>
                    <div>Drag & drop a .zip file here or click to upload</div>

                    <CFormInput
                      type="file"
                      id="inputFile"
                      accept=".zip,.zipx,.z01"
                      onChange={(e) => {
                        const file = e.target.files[0]
                        if (file) {
                          const fileName = file.name.toLowerCase()
                          if (fileName.endsWith('.zip')) {
                            setSelectedFile(file)
                            setFile(file)
                            setErrors((prev) => ({ ...prev, file: '' }))
                          } else {
                            setErrors((prev) => ({ ...prev, file: 'Only .zip files are allowed.' }))
                          }
                        }
                      }}
                      style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                        top: 0,
                        left: 0,
                      }}
                    />
                  </>
                )}
              </div>
              {errors.file && (
                <div
                  style={{
                    color: 'red',
                    marginTop: '5px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {errors.file}
                </div>
              )}
            </CInputGroup>
            {errors.general && (
              <div style={{ color: 'red', marginTop: '15px' }}>{errors.general}</div>
            )}
            {errors.general && errors.general.includes('valid') && (
              <>
                <br />
                <p>The valid package files for supported frameworks are listed below:-</p>
                <ul>
                  <li>
                    <strong>pom.xml</strong>: For Maven based Java projects
                  </li>
                  <li>
                    <strong>composer.json</strong>: For Composer based PHP projects
                  </li>
                  <li>
                    <strong>requirements.txt</strong>: For Python projects
                  </li>
                  <li>
                    <strong>package.json</strong>: For Javascript/Typescript or other NPM based
                    projects
                  </li>
                  <li>
                    <strong>Gemfile</strong>: For Ruby projects
                  </li>
                  <li>
                    <strong>Package.swift</strong>: For Swift projects
                  </li>
                  <li>
                    <strong>go.mod</strong>: For Go projects
                  </li>
                  <li>
                    <strong>Podfile</strong>: For CocoaPods projects using Objective C or Swift
                  </li>
                  <li>
                    <strong>build.gradle</strong>: For Gradle based Java projects
                  </li>
                  <li>
                    <strong>
                      packages.config, nuget.config, or a file with extension .csproj{' '}
                    </strong>
                    : For C# based .NET projects
                  </li>
                </ul>
              </>
            )}
            <CButton
              style={{
                width: '100%',
                marginTop: '3%',
                marginBottom: '2%',
                borderWidth: 0,
                fontSize: 13,
                background: '#e50202',
              }}
              color="primary"
              className="px-3"
              onClick={startScan}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress
                  color="primary"
                  size={24}
                  style={{ marginTop: 10, color: '#fff' }}
                />
              ) : (
                'Start Scan'
              )}
            </CButton>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  )
}

export default StartSCAScan
